// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { useOtChainState, useOtSelector } from '@react/redux/store'
import { EthAddr } from '@shared/SchemaTypes'
import dayjs, { Dayjs } from 'dayjs'
import React, { useState } from 'react'

import { Box, Typography } from '@mui/material'

import { DateRange, DateRangePicker, LocalizationProvider, PickersShortcutsItem } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'

export default function ReportDateRangeSelector(props: {
  selectedPoolAddrs: EthAddr[]
  onSelectedChange: (dayRange: [Dayjs, Dayjs]) => void
  forEvents: boolean
  initialDateRange?: [Dayjs, Dayjs]
  allowFutureDates?: boolean
  isConsolidatedReport?: boolean
}): React.ReactElement {
  const {
    chainConfiguration: { chainTimestampDelta },
  } = useOtChainState()
  const { poolOverviewCTOs } = useOtSelector((state) => state.poolState)

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(
    props.initialDateRange ?? [
      dayjs().utc().subtract(7, 'day').add(chainTimestampDelta, 'seconds').startOf('day'),
      dayjs().utc().subtract(1, 'day').add(chainTimestampDelta, 'seconds').startOf('day'),
    ]
  )

  let minDate = dayjs().utc().subtract(1000, 'day').add(chainTimestampDelta, 'seconds').startOf('day')
  if (!props.isConsolidatedReport) {
    props.selectedPoolAddrs.forEach((poolAddr) => {
      if (poolOverviewCTOs[poolAddr]?.creationTimestamp) {
        const minDate2 = dayjs((poolOverviewCTOs[poolAddr]?.creationTimestamp ?? 0) * 1000)
          .utc()
          .add(1, 'day')
          .startOf('day')

        if (minDate2 > minDate) {
          minDate = minDate2
        }
      }
    })
  }

  const localSetDateRange = (newDateRange: DateRange<Dayjs>) => {
    if (setDateRange && newDateRange[0] && newDateRange[1]) {
      setDateRange([newDateRange[0], newDateRange[1]])
      props.onSelectedChange([newDateRange[0], newDateRange[1]])
    }
  }
  function startOfDayAgo(daysAgo: number): Dayjs {
    return dayjs().utc().subtract(daysAgo, 'day').add(chainTimestampDelta, 'seconds').startOf('day')
  }
  function nowDayjs(): Dayjs {
    if (props.forEvents) {
      return dayjs().utc().add(chainTimestampDelta, 'seconds')
    }
    return dayjs().utc().add(chainTimestampDelta, 'seconds').startOf('day')
  }

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: 'Last 7 Days',
      getValue: () => {
        return [startOfDayAgo(7), props.forEvents ? nowDayjs() : startOfDayAgo(1)]
      },
    },
    {
      label: 'Last 30 Days',
      getValue: () => {
        return [startOfDayAgo(30), props.forEvents ? nowDayjs() : startOfDayAgo(1)]
      },
    },
    {
      label: 'This Month',
      getValue: () => {
        return [
          dayjs().add(chainTimestampDelta, 'seconds').utc().startOf('month').startOf('day'),
          props.forEvents ? nowDayjs() : startOfDayAgo(1),
        ]
      },
    },
    {
      label: 'Last Month',
      getValue: () => {
        return [
          dayjs().add(chainTimestampDelta, 'seconds').utc().subtract(1, 'month').startOf('month').startOf('day'),
          dayjs().add(chainTimestampDelta, 'seconds').utc().startOf('month').subtract(1, 'day').startOf('day'),
        ]
      },
    },
    {
      label: 'current year',
      getValue: () => {
        return [
          dayjs().add(chainTimestampDelta, 'seconds').utc().startOf('year').startOf('day'),
          props.forEvents ? nowDayjs() : startOfDayAgo(1),
        ]
      },
    },
    {
      label: '1 year',
      getValue: () => {
        return [startOfDayAgo(365), props.forEvents ? nowDayjs() : startOfDayAgo(1)]
      },
    },
  ]

  const maxDate = props.allowFutureDates
    ? dayjs().utc().add(2, 'year').add(chainTimestampDelta, 'seconds').startOf('day')
    : props.forEvents
      ? nowDayjs()
      : dayjs().utc().subtract(1, 'day').add(chainTimestampDelta, 'seconds').startOf('day')

  return (
    <Box sx={{ flexGrow: 0, width: 275, flexDirection: 'column', display: 'flex', marginBottom: 6 }}>
      <Typography sx={{ marginTop: -1, marginBottom: 1, height: 24 }} variant="caption">
        Date Range
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="UTC">
        <DateRangePicker
          sx={{
            height: 40,
          }}
          slotProps={{
            shortcuts: {
              items: shortcutsItems,
            },
            actionBar: { actions: [] },
          }}
          minDate={minDate}
          calendars={2}
          maxDate={maxDate}
          label={'Date Range'}
          value={dateRange}
          onChange={(newDateRange) => {
            localSetDateRange(newDateRange)
          }}
        />
      </LocalizationProvider>
    </Box>
  )
}
